import { _ as _extends } from '../../_rollupPluginBabelHelpers-c245b26a.js';
import { forwardRef, useState, useRef, useCallback } from 'react';
import { useMergeRefs } from '../../hooks/useMergeRefs.js';
import { useControllableState } from '../../hooks/useControllableState.js';
import { useOutsideInteraction } from '../../hooks/useOutsideInteraction.js';
import isFunction from '../../utils/isFunction.js';
import { DisclosurePopupProvider } from './context.js';
import { jsx } from '@emotion/react';
import { Box } from '../Box/Box.js';
import '../../utils/warn.js';
import '@indeed/ifl-css';

/**
 * @see https://experience.indeed.design/components/ids/disclosure-popup
 */
const DisclosurePopup = /*#__PURE__*/forwardRef((props, forwardedRef) => {
  const {
    children,
    defaultOpen,
    open,
    onOpenChange,
    portaledPopup = false,
    ...remainingProps
  } = props;
  const [isOpen, setIsOpen] = useControllableState({
    state: open,
    defaultState: defaultOpen,
    initialState: false
  });
  const [anchor, setAnchor] = useState(null);
  const [contentId, setContentId] = useState("");
  const contentRef = useRef(null);
  const triggerRef = useRef(null);
  const containerRef = useRef(null);
  const mergedContainerRef = useMergeRefs(forwardedRef, containerRef);
  const setOpen = useCallback(newIsOpen => {
    onOpenChange == null || onOpenChange(newIsOpen);
    setIsOpen(newIsOpen);
  }, [onOpenChange, setIsOpen]);
  const handleOutsideInteraction = () => {
    setOpen(false);
  };
  useOutsideInteraction({
    refs: [contentRef, triggerRef],
    onOutsideInteraction: handleOutsideInteraction,
    disabled: !isOpen
  });
  const context = {
    anchor,
    contentId,
    contentRef,
    isOpen,
    portaledPopup,
    setAnchor,
    setContentId,
    setOpen,
    triggerRef
  };
  return jsx(DisclosurePopupProvider, {
    value: context
  }, jsx(Box, _extends({
    as: "div",
    ref: mergedContainerRef
  }, remainingProps), isFunction(children) ? children({
    isOpen
  }) : children));
});
DisclosurePopup.displayName = "DisclosurePopup";

export { DisclosurePopup };
